import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Chip,
    Grid,
    CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { eliminarSiniestro } from '../../services/art/siniestroService';

export default function ModalDetalleSiniestro({ isOpen, onClose, siniestro, onEdit, onDelete }) {
    const [confirmandoEliminar, setConfirmandoEliminar] = useState(false);
    const [eliminando, setEliminando] = useState(false);

    if (!siniestro) return null;

    const handleEliminar = async () => {
        setEliminando(true);
        try {
            await eliminarSiniestro(siniestro.idSiniestro);
            if (onDelete) onDelete();
            setConfirmandoEliminar(false);
            onClose();
        } catch (err) {
            console.error('Error al eliminar siniestro:', err);
            alert(err.response?.data || 'Error al eliminar el siniestro.');
        } finally {
            setEliminando(false);
        }
    };

    const Campo = ({ label, valor }) => (
        <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>
                {valor || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No especificado</span>}
            </Typography>
        </Box>
    );

    return (
        <>
            {/* Modal principal de detalle */}
            <Dialog 
                open={isOpen && !confirmandoEliminar} 
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    fontWeight: 'bold',
                    textAlign: 'center'
                }}>
                    Detalle del Siniestro
                </DialogTitle>

                <DialogContent sx={{ mt: 2, pb: 1 }}>
                    <Grid container spacing={3}>
                        {/* Columna izquierda */}
                        <Grid item xs={12} md={6}>
                            <Campo label="Nro. Siniestro" valor={siniestro.nroSiniestro} />
                            <Campo label="Nro. Expediente SRT" valor={siniestro.nroExpedienteSRT} />
                            <Campo label="Tipo de Contingencia" valor={
                                siniestro.tipoContingencia ? (
                                    <Chip label={siniestro.tipoContingencia} size="small" variant="outlined" />
                                ) : null
                            } />
                        </Grid>

                        {/* Columna derecha */}
                        <Grid item xs={12} md={6}>
                            <Campo label="Respuesta ART" valor={siniestro.respuestaART} />
                            <Campo label="Lesiones" valor={(() => {
                                const lista = Array.isArray(siniestro.nombresLesiones) && siniestro.nombresLesiones.length > 0
                                    ? siniestro.nombresLesiones
                                    : (siniestro.nombreLesion ? [siniestro.nombreLesion] : []);
                                if (lista.length === 0) return null;
                                return (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {lista.map((nombre, i) => (
                                            <Chip key={i} label={nombre} size="small" variant="outlined" />
                                        ))}
                                    </Box>
                                );
                            })()} />
                            <Campo label="Médicos asignados" valor={
                                siniestro.nombresMedicos && siniestro.nombresMedicos.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {siniestro.nombresMedicos.map((nombre, i) => (
                                            <Chip key={i} label={nombre} size="small" variant="outlined" />
                                        ))}
                                    </Box>
                                ) : null
                            } />
                        </Grid>
                    </Grid>

                    {/* Comentario - ancho completo */}
                    {siniestro.comentario && (
                        <Box sx={{ mt: 3, p: 2, borderRadius: 2, backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 1 }}>
                                Comentario
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                {siniestro.comentario}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 3, gap: 1, justifyContent: 'space-between' }}>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => setConfirmandoEliminar(true)}
                        sx={{ borderRadius: '20px' }}
                    >
                        Eliminar
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                            variant="outlined" 
                            onClick={onClose}
                            sx={{ borderRadius: '20px' }}
                        >
                            Cerrar
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={() => { if (onEdit) onEdit(siniestro); }}
                            sx={{ borderRadius: '20px' }}
                        >
                            Editar
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* Modal de confirmación de eliminación */}
            <Dialog open={confirmandoEliminar} onClose={() => setConfirmandoEliminar(false)}>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    Confirmar eliminación
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Está seguro de eliminar el siniestro <strong>{siniestro.nroSiniestro}</strong>?
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                        Esta acción no se puede deshacer.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setConfirmandoEliminar(false)}
                        disabled={eliminando}
                        sx={{ borderRadius: '20px' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleEliminar}
                        disabled={eliminando}
                        sx={{ borderRadius: '20px' }}
                    >
                        {eliminando ? (
                            <><CircularProgress size={18} sx={{ mr: 1 }} />Eliminando...</>
                        ) : (
                            <>Eliminar</>
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

